import { type ReactNode, useEffect, useState } from 'react';
import { beginLogin, getCurrentUserEmail, isAuthenticated, logout } from './cognito';

interface AuthGateProps {
  children: ReactNode;
}

export default function AuthGate({ children }: AuthGateProps) {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    try {
      setAuthed(isAuthenticated());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Auth initialization failed');
      setAuthed(false);
    } finally {
      setReady(true);
    }
  }, []);

  if (!ready) {
    return <div style={{ padding: '2rem' }}>Loading authentication...</div>;
  }

  if (!authed) {
    const handleSignIn = async () => {
      setError('');
      setSigningIn(true);
      try {
        await beginLogin(window.location.pathname + window.location.search);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to start sign in');
        setSigningIn(false);
      }
    };

    return (
      <div style={{ padding: '2rem', maxWidth: '520px', margin: '0 auto' }}>
        <h1>Sign in required</h1>
        <p>Use your GraceHill staff account to access churchplanter.io.</p>
        {error ? <p style={{ color: '#c62828' }}>{error}</p> : null}
        <button
          type="button"
          onClick={handleSignIn}
          disabled={signingIn}
          style={{
            marginTop: '1rem',
            padding: '0.6rem 1rem',
            borderRadius: '6px',
            border: 'none',
            background: '#2f3f9e',
            color: '#fff',
            cursor: signingIn ? 'wait' : 'pointer',
            opacity: signingIn ? 0.85 : 1,
          }}
        >
          {signingIn ? 'Starting sign in...' : 'Sign in with GraceHill Auth'}
        </button>
      </div>
    );
  }

  const email = getCurrentUserEmail();

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: '12px',
          right: '12px',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          background: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #e0e0e0',
          borderRadius: '999px',
          padding: '0.4rem 0.6rem',
        }}
      >
        <span style={{ fontSize: '0.8rem', color: '#555' }}>{email || 'Signed in'}</span>
        <button
          onClick={logout}
          style={{
            border: 'none',
            background: '#f5f5f5',
            borderRadius: '999px',
            padding: '0.25rem 0.55rem',
            cursor: 'pointer',
            fontSize: '0.75rem',
          }}
        >
          Logout
        </button>
      </div>
      {children}
    </>
  );
}

